// ══════════════════════════════════════════════════════════════════════════════════════════════
// CE QU'UNE NOTIFICATION DIT — la moitié TEXTE du centre, et elle vit ICI parce qu'elle ne peut pas
// vivre au serveur.
//
// ⚠️ 🔴 POURQUOI L'ÉCRAN COMPOSE LA PHRASE. Un texte rendu par le serveur ne passe par AUCUN catalogue
// de traduction : il s'affiche en français à un compte anglais, et aucune garde du web ne peut le voir
// puisque la chaîne n'y vit pas. Le serveur envoie donc un FAIT (`kind` + `params`) et cet écran le
// nomme avec `t()` — exactement le partage retenu pour `whyCode` (doublons d'articles) et pour les
// soldes. C'est la raison d'être de ce module : sans lui, chaque producteur écrirait des phrases.
//
// ⚠️ 🔴 LA LISTE DES FAITS VIT DANS LE SERVEUR (`notifications/catalog.ts`), pas ici. Les deux moitiés
// ne peuvent pas partager un type : le web du BO n'importe jamais du serveur du BO (mesuré), et le
// contrat `@orgazone/sync-contract` dit lui-même ce qu'il est — « device ↔ serveur » —, or une
// notification interne du siège n'y transite jamais. Ce que ça coûte est GARDÉ de deux façons :
// `scripts/audit-notifications-faits.mjs` (chaîné au lint) refuse un fait sans phrase ET une phrase
// sans fait ; et un `kind` inconnu se rend EN CLAIR plutôt qu'en blanc, donc une divergence dégrade
// VISIBLEMENT au lieu de se taire.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import type { NotificationItem } from './api';
import { moisLongLisible } from './periode';
import { t } from './i18n';

/** Ce qu'une ligne du panneau affiche. */
export interface PhraseDuFait {
  title: string;
  /** L'explication, ou `null` quand le titre se suffit. */
  body: string | null;
}

/** Une chaîne de `params`, ou `null` si elle manque — un paramètre absent n'a pas de valeur de repli. */
const texte = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

/**
 * Un nombre de `params`, ou `null`.
 * ⚠️ 🔴 JAMAIS 0 PAR DÉFAUT : « 0 pièce scellée » serait une donnée INVENTÉE rendue comme réelle, et
 * sur un centre d'alerte c'est exactement le chiffre qu'on croit sur parole (§23.0). L'absence se
 * propage, elle ne se comble pas.
 */
const nombre = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * LES FAITS QUE CET ÉCRAN SAIT NOMMER.
 *
 * ⚠️ 🔴 UNE PHRASE REND `null` QUAND SES PARAMÈTRES MANQUENT, et l'appelant retombe alors sur le repli
 * honnête. Une phrase à trous (« la caisse a scellé pièces sur ce mois ») serait pire qu'un code
 * technique : elle a l'air d'une vraie information.
 */
const PHRASES: Record<string, (p: Record<string, unknown>) => PhraseDuFait | null> = {
  'fiscal.closure_overdue': (p) => {
    const registre = texte(p.registerId);
    const mois = texte(p.month);
    const dernier = texte(p.lastSealedMonth);
    const pieces = nombre(p.pieces);
    if (!registre || !mois || !dernier || pieces === null) return null;
    return {
      // ⚠️ LE MOIS EST RENDU DANS LA LANGUE DU COMPTE par `Intl`, et EN MINUSCULES : c'est sa forme
      //    juste au milieu d'une phrase française. Ce n'est pas de la prose passée en valeur (Forme
      //    DS) mais une DONNÉE — un nom de mois, produit par la plateforme, pas une demi-phrase.
      title: t('Clôture de {v} manquante', { v: moisLongLisible(mois) }),
      body: t(
        'La caisse {caisse} a scellé {n} pièce sur ce mois, puis a vendu jusqu’en {jusqu} sans jamais le clôturer.',
        { n: pieces, caisse: registre, jusqu: moisLongLisible(dernier) },
      ),
    };
  },
};

/**
 * LA PHRASE D'UN FAIT — ou l'aveu qu'on ne sait pas le nommer.
 *
 * ⚠️ 🔴 UN `kind` INCONNU SE REND EN CLAIR, JAMAIS EN BLANC NI MASQUÉ. Ce cas arrive pour de bon : le
 * serveur peut être plus récent que le bundle chargé dans le navigateur. Cacher la ligne ferait
 * disparaître une alerte qui compte pourtant dans la pastille — un compteur qui ne correspond à rien de
 * visible est la pire chose qu'une cloche puisse faire (§23.0). On montre donc le code technique et on
 * dit pourquoi.
 */
export function phraseDuFait(item: Pick<NotificationItem, 'kind' | 'params'>): PhraseDuFait {
  const phrase = PHRASES[item.kind]?.(item.params) ?? null;
  if (phrase) return phrase;
  return {
    title: item.kind,
    body: t('Cette version de l’application ne sait pas encore nommer ce fait. Rechargez la page ; s’il persiste, signalez ce code.'),
  };
}
